import { useCallback, useMemo } from 'react'
import { Ban, Bot, KeyRound, ShieldCheck } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import {
  fetchApps,
  setAppStatus,
  fetchBotTokens,
  revokeBotToken,
  DEVX_STR as S,
  type AppRow,
  type BotTokenRow,
} from './devApiExt'

/**
 * A13.05 — Ilovani bloklash. Fokuslangan blok/ochish jadvali (REAL: mavjud
 * `POST /admin/developers/apps/:id/status`). Placeholder permission USERS_VIEW.
 */
export function AppBlockScreen() {
  const { toast } = useToast()

  const columns = useMemo<AdminColumn<AppRow>[]>(
    () => [
      { key: 'name', header: S.name, type: 'text' },
      { key: 'api_id', header: 'api_id', type: 'number' },
      { key: 'owner', header: S.owner, type: 'text' },
      { key: 'status', header: S.status, type: 'text' },
      { key: 'requests_today', header: S.requestsToday, type: 'number' },
    ],
    [],
  )

  const changeStatus = useCallback(
    async (row: AppRow, status: 'active' | 'blocked') => {
      try {
        await setAppStatus(row.id, status)
        toast(S.statusChanged, { tone: 'success' })
      } catch {
        toast(S.failed, { tone: 'error' })
      }
    },
    [toast],
  )

  const rowActions = useMemo<RowAction<AppRow>[]>(
    () => [
      {
        id: 'block',
        label: S.block,
        icon: <Ban size={14} />,
        danger: true,
        visible: (r) => r.status === 'active',
        onSelect: (r) => changeStatus(r, 'blocked'),
      },
      {
        id: 'unblock',
        label: S.unblock,
        icon: <ShieldCheck size={14} />,
        visible: (r) => r.status === 'blocked',
        onSelect: (r) => changeStatus(r, 'active'),
      },
    ],
    [changeStatus],
  )

  const fetcher = useCallback(fetchApps, [])
  return (
    <>
      <PageHeader title={S.appBlock} screenId="A13.05" description={S.appBlockDesc} />
      <DataTable<AppRow>
        screenId="A13.05"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <Ban size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}

/**
 * A13.09 — Majburiy revoke. Bot-token darhol bekor qilish jadvali (REAL: mavjud
 * `POST /admin/developers/bot-tokens/:id/revoke`). Placeholder permission USERS_VIEW.
 */
export function ForcedRevokeScreen() {
  const { toast } = useToast()

  const columns = useMemo<AdminColumn<BotTokenRow>[]>(
    () => [
      { key: 'bot_username', header: S.bot, type: 'text' },
      { key: 'owner', header: S.owner, type: 'text' },
      { key: 'last_used', header: S.lastUsed, type: 'text' },
      { key: 'requests', header: S.requests, type: 'number' },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<BotTokenRow>[]>(
    () => [
      {
        id: 'revoke',
        label: S.revoke,
        icon: <KeyRound size={14} />,
        danger: true,
        visible: (r) => !r.revoked,
        onSelect: async (r) => {
          try {
            await revokeBotToken(r.id)
            toast(S.revoked, { tone: 'success' })
          } catch {
            toast(S.failed, { tone: 'error' })
          }
        },
      },
    ],
    [toast],
  )

  const fetcher = useCallback(fetchBotTokens, [])
  return (
    <>
      <PageHeader title={S.forcedRevoke} screenId="A13.09" description={S.forcedRevokeDesc} />
      <DataTable<BotTokenRow>
        screenId="A13.09"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <Bot size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
